/**
 * 動画サムネイル生成（Web 動画ポスト投稿用・タスク32.4）。
 *
 * ブラウザで動画ファイルを読み込み、先頭付近の1フレームを Canvas に描いて
 * JPEG の Blob として返す。ピン・OGP に使うサムネを投稿前にクライアントで作る。
 * 失敗時は null（サムネ無しポスト＝🎬 アイコンにフォールバック）。
 */

/** サムネの長辺上限（px）。 */
const MAX_EDGE = 640;
/** 切り出すフレームの時刻（秒）。先頭は黒いことがあるので少し進める。 */
const SEEK_SEC = 0.5;
const QUALITY = 0.82;

export async function generateVideoThumbnail(file: File): Promise<Blob | null> {
  if (!file.type.startsWith('video/')) {
    return null;
  }
  const url = URL.createObjectURL(file);
  try {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.src = url;

    await new Promise<void>((resolve, reject) => {
      const onError = () => reject(new Error('video_load_failed'));
      video.addEventListener('loadeddata', () => resolve(), { once: true });
      video.addEventListener('error', onError, { once: true });
    });

    // 指定時刻へシーク（動画長が短い場合は 0 にクランプ）。
    const seekTo = Math.min(SEEK_SEC, (video.duration || 1) / 2);
    await new Promise<void>((resolve, reject) => {
      video.addEventListener('seeked', () => resolve(), { once: true });
      video.addEventListener('error', () => reject(new Error('seek_failed')), {
        once: true,
      });
      video.currentTime = seekTo;
    });

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) {
      return null;
    }
    const scale = vw > MAX_EDGE || vh > MAX_EDGE ? MAX_EDGE / Math.max(vw, vh) : 1;
    const w = Math.round(vw * scale);
    const h = Math.round(vh * scale);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }
    ctx.drawImage(video, 0, 0, w, h);

    return await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', QUALITY);
    });
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}
